/**
 * Footer - Landing page footer
 *
 * Wordmark + tagline, language pairs, links, copyright.
 * v10-A5/A6: tagline + link labels route through landing.footer.* i18n keys
 * (locale from ?lang / ?ref hint / navigator.languages — see LandingI18n.tsx).
 */

import { useLandingT, useLandingLocale, usePublicHref } from './landing-locale'
import { isPublicLocale } from '../../lib/public-locale'

export function Footer() {
  const t = useLandingT()
  const locale = useLandingLocale()
  const href = usePublicHref()

  // Blog hub (BlogIndexView) — the per-market entry to all posts. Posts
  // themselves live at /blog/<slug>/ regardless of language; the hub is
  // where the locale prefix lives (docs/plans/seo-signal-fixes.md).
  const blogHub = isPublicLocale(locale) ? `/${locale}/blog` : '/blog'

  const LINKS = [
    { label: t('landing.footer.blog'), href: blogHub },
    { label: t('landing.footer.privacy'), href: href('/privacy') },
    { label: t('landing.footer.terms'), href: href('/terms') },
    { label: t('landing.footer.contact'), href: 'mailto:support@piccard.app' },
  ] as const

  return (
    <footer className="py-12 bg-surface-container-low border-t border-outline-variant">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col md:flex-row items-center justify-between gap-6">
          {/* Logo + tagline */}
          <div className="text-center md:text-left">
            <span className="font-headline font-extrabold text-lg text-primary">Piccard</span>
            <p className="text-muted-soft text-sm mt-1">{t('landing.footer.tagline')}</p>
          </div>

          {/* Links */}
          <div className="flex items-center gap-6">
            {LINKS.map((link) => (
              <a
                key={link.label}
                href={link.href}
                className="text-muted-soft text-sm hover:text-on-surface transition-colors"
              >
                {link.label}
              </a>
            ))}
          </div>
        </div>

        <div className="mt-8 pt-6 border-t border-outline-variant text-center">
          <p className="text-muted-soft text-xs">
            &copy; {new Date().getFullYear()} Piccard. {t('landing.footer.rights')}
          </p>
        </div>
      </div>
    </footer>
  )
}
